import { createReducer } from 'typesafe-actions'

import {
  ISfBankAccount,
  ISfWallet,
  IUserBankAccount,
  IUserWallet,
  ReadClientTypesForAssignToUserResponseDto,
  ReadUserBankAccountsRequestDto,
  ReadUserSfBankAccountRequestDto,
  ReadUserSfWalletsRequestDto,
  ReadUserWalletsRequestDto,
  WebAccessTokenPayloadDto,
} from '@sparling/financial-common'
import { ActionTypes } from '../types'
import {
  getUserRequest,
  getUserSuccess,
  getUserFailure,
  readUserBankAccountsRequest,
  readUserBankAccountsSuccess,
  readUserBankAccountsFailure,
  readUserWalletsRequest,
  readUserWalletsSuccess,
  readUserWalletsFailure,
  readUserSfBankAccountsRequest,
  readUserSfBankAccountsSuccess,
  readUserSfBankAccountsFailure,
  readUserSfWalletsRequest,
  readUserSfWalletsSuccess,
  readUserSfWalletsFailure,
  createUserWalletRequest,
  createUserWalletSuccess,
  confirmReceivedAmountSuccess,
  userBaseCurrencyUpdated,
  loginRequest,
  loginSuccess,
  loginFailure,
  login2FASuccess,
  login2FAFailure,
  login2FARequest,
  getUserWalletListRequest,
  getUserWalletListSuccess,
  getUserWalletListFailure,
  createUserWalletFailure,
  userExternalWalletStatusUpdated,
  getClientTypesForAssignToUserFailure,
  getClientTypesForAssignToUserRequest,
  getClientTypesForAssignToUserSuccess,
  getUserBankAccountListRequest,
  getUserBankAccountListSuccess,
  getUserBankAccountListFailure,
} from '../actions'
import { LoadingState } from '../../types'

export type UserState = {
  loggingIn: LoadingState
  status: LoadingState
  data: WebAccessTokenPayloadDto | null
  error: Error | null
  bankAccounts: {
    status: LoadingState
    filters: ReadUserBankAccountsRequestDto
    data: {
      items: IUserBankAccount[]
      meta: {
        itemCount: number
        itemsPerPage: number
        totalItems: number
        totalPages: number
        currentPage: number
      }
    }
    error: Error | null
  }
  wallets: {
    status: LoadingState
    filters: ReadUserWalletsRequestDto
    data: {
      items: IUserWallet[]
      meta: {
        itemCount: number
        itemsPerPage: number
        totalItems: number
        totalPages: number
        currentPage: number
      }
    }
    error: Error | null
  }
  sfBankAccounts: {
    status: LoadingState
    filters: Pick<ReadUserSfBankAccountRequestDto, 'count' | 'page'>
    data: {
      items: ISfBankAccount[]
      meta: {
        itemCount: number
        itemsPerPage: number
        totalItems: number
        totalPages: number
        currentPage: number
      }
    }
    error: Error | null
  }
  sfWallets: {
    status: LoadingState
    filters: Pick<ReadUserSfWalletsRequestDto, 'count' | 'page'>
    data: {
      items: ISfWallet[]
      meta: {
        itemCount: number
        itemsPerPage: number
        totalItems: number
        totalPages: number
        currentPage: number
      }
    }
    error: Error | null
  }
  userWalletsList: {
    status: LoadingState
    data: IUserWallet[]
    error: Error | null
  }
  userBankAccountList: {
    status: LoadingState
    data: IUserBankAccount[]
    error: Error | null
  }
  clientTypesForAssignToUser: {
    status: LoadingState
    data: ReadClientTypesForAssignToUserResponseDto
    error: Error | null
  }
}

export const initialState: UserState = {
  loggingIn: 'IDLE',
  status: 'IDLE',
  data: null,
  error: null,
  bankAccounts: {
    status: 'IDLE',
    filters: {
      limit: 10,
    },
    data: {
      items: [],
      meta: {
        itemCount: 0,
        itemsPerPage: 0,
        totalItems: 0,
        totalPages: 0,
        currentPage: 0,
      },
    },
    error: null,
  },
  wallets: {
    status: 'IDLE',
    filters: {
      limit: 10,
    },
    data: {
      items: [],
      meta: {
        itemCount: 0,
        itemsPerPage: 0,
        totalItems: 0,
        totalPages: 0,
        currentPage: 0,
      },
    },
    error: null,
  },
  sfBankAccounts: {
    status: 'IDLE',
    filters: {
      count: 10,
    },
    data: {
      items: [],
      meta: {
        itemCount: 0,
        itemsPerPage: 0,
        totalItems: 0,
        totalPages: 0,
        currentPage: 0,
      },
    },
    error: null,
  },
  sfWallets: {
    status: 'IDLE',
    filters: {
      count: 10,
    },
    data: {
      items: [],
      meta: {
        itemCount: 0,
        itemsPerPage: 0,
        totalItems: 0,
        totalPages: 0,
        currentPage: 0,
      },
    },
    error: null,
  },
  userWalletsList: {
    status: 'IDLE',
    data: [],
    error: null,
  },
  userBankAccountList: {
    status: 'IDLE',
    data: [],
    error: null,
  },
  clientTypesForAssignToUser: {
    status: 'IDLE',
    data: {
      items: [],
      meta: {
        itemCount: 0,
        itemsPerPage: 0,
        totalItems: 0,
        totalPages: 0,
        currentPage: 0,
      },
    },
    error: null,
  },
}

export const user = createReducer<UserState, ActionTypes>(initialState)
  .handleAction([loginRequest, login2FARequest], (state) => ({ ...state, loggingIn: 'LOADING' }))
  .handleAction([loginSuccess, login2FASuccess], (state) => ({ ...state, loggingIn: 'SUCCESS' }))
  .handleAction([loginFailure, login2FAFailure], (state) => ({ ...state, loggingIn: 'ERROR' }))
  .handleAction(getUserRequest, (state) => ({ ...state, status: 'LOADING' }))
  .handleAction(getUserSuccess, (state, { payload }) => ({
    ...state,
    status: payload ? 'SUCCESS' : 'IDLE',
    data: payload ? { ...payload } : null,
  }))
  .handleAction(getUserFailure, (state, { payload }) => ({ ...state, status: 'ERROR', error: payload }))
  .handleAction(readUserBankAccountsRequest, (state, { payload }) => ({
    ...state,
    bankAccounts: {
      ...state.bankAccounts,
      filters: {
        ...state.bankAccounts.filters,
        ...(payload.page && { page: Math.min(state.bankAccounts.data.meta?.totalPages, payload.page) }),
      },
      status: 'LOADING',
    },
  }))
  .handleAction(readUserBankAccountsSuccess, (state, { payload }) => ({
    ...state,
    bankAccounts: {
      ...state.bankAccounts,
      status: 'SUCCESS',
      data: payload.data,
    },
  }))
  .handleAction(readUserBankAccountsFailure, (state) => ({
    ...state,
    bankAccounts: {
      ...state.bankAccounts,
      status: 'ERROR',
      data: initialState.bankAccounts.data,
    },
  }))
  .handleAction(createUserWalletRequest, (state) => ({
    ...state,
    wallets: {
      ...state.wallets,
      status: 'LOADING',
    },
  }))
  .handleAction(createUserWalletSuccess, (state, { payload }) => ({
    ...state,
    wallets: {
      ...state.wallets,
      status: 'SUCCESS',
      data: {
        ...state.wallets.data,
        items: [...state.wallets.data.items, payload.data],
      },
    },
  }))
  .handleAction(createUserWalletFailure, (state) => ({
    ...state,
    wallets: {
      ...state.wallets,
      status: 'ERROR',
    },
  }))
  .handleAction(readUserWalletsRequest, (state, { payload }) => ({
    ...state,
    wallets: {
      ...state.wallets,
      filters: {
        ...state.wallets.filters,
        ...(payload.page && { page: Math.min(state.wallets.data.meta?.totalPages, payload.page) }),
      },
      status: 'LOADING',
    },
  }))
  .handleAction(readUserWalletsSuccess, (state, { payload }) => ({
    ...state,
    wallets: {
      ...state.wallets,
      status: 'SUCCESS',
      data: payload.data,
    },
  }))
  .handleAction(readUserWalletsFailure, (state) => ({
    ...state,
    wallets: {
      ...state.wallets,
      status: 'ERROR',
      data: initialState.wallets.data,
    },
  }))
  .handleAction(confirmReceivedAmountSuccess, (state, { payload }) => ({
    ...state,
    wallets: {
      ...state.wallets,
      data: {
        ...state.wallets.data,
        items: state.wallets.data.items.map((wallet) => {
          if (wallet.id === payload.walletId) {
            return {
              ...wallet,
              status: payload.walletStatus,
            }
          }
          return wallet
        }),
      },
    },
  }))
  .handleAction(userExternalWalletStatusUpdated, (state, { payload }) => ({
    ...state,
    wallets: {
      ...state.wallets,
      data: {
        ...state.wallets.data,
        items: state.wallets.data.items.map((wallet) => {
          if (wallet.id === payload.walletId) {
            return {
              ...wallet,
              status: payload.status,
            }
          }
          return wallet
        }),
      },
    },
  }))
  .handleAction(readUserSfBankAccountsRequest, (state, { payload }) => ({
    ...state,
    sfBankAccounts: {
      ...state.sfBankAccounts,
      filters: {
        ...state.sfBankAccounts.filters,
        ...(payload.page && { page: Math.min(state.sfBankAccounts.data.meta?.totalPages, payload.page) }),
      },
      status: 'LOADING',
    },
  }))
  .handleAction(readUserSfBankAccountsSuccess, (state, { payload }) => ({
    ...state,
    sfBankAccounts: {
      ...state.sfBankAccounts,
      status: 'SUCCESS',
      data: payload,
    },
  }))
  .handleAction(readUserSfBankAccountsFailure, (state) => ({
    ...state,
    sfBankAccounts: {
      ...state.sfBankAccounts,
      status: 'ERROR',
      data: initialState.sfBankAccounts.data,
    },
  }))
  .handleAction(readUserSfWalletsRequest, (state, { payload }) => ({
    ...state,
    sfWallets: {
      ...state.sfWallets,
      filters: {
        ...state.sfWallets.filters,
        ...(payload.page && { page: Math.min(state.sfWallets.data.meta?.totalPages, payload.page) }),
      },
      status: 'LOADING',
    },
  }))
  .handleAction(readUserSfWalletsSuccess, (state, { payload }) => ({
    ...state,
    sfWallets: {
      ...state.sfWallets,
      status: 'SUCCESS',
      data: payload,
    },
  }))
  .handleAction(readUserSfWalletsFailure, (state) => ({
    ...state,
    sfWallets: {
      ...state.sfWallets,
      status: 'ERROR',
      data: initialState.sfWallets.data,
    },
  }))
  .handleAction(userBaseCurrencyUpdated, (state, { payload }) => ({
    ...state,
    data: {
      ...state.data,
      currency: payload,
    } as WebAccessTokenPayloadDto,
  }))
  .handleAction(getUserWalletListRequest, (state) => ({
    ...state,
    userWalletsList: {
      ...state.userWalletsList,
      status: 'LOADING',
    },
  }))
  .handleAction(getUserWalletListSuccess, (state, { payload }) => ({
    ...state,
    userWalletsList: {
      ...state.userWalletsList,
      status: 'SUCCESS',
      data: payload,
    },
  }))
  .handleAction(getUserWalletListFailure, (state, { payload }) => ({
    ...state,
    userWalletsList: {
      ...state.userWalletsList,
      status: 'ERROR',
      error: payload,
    },
  }))
  .handleAction(getUserBankAccountListRequest, (state) => ({
    ...state,
    userBankAccountList: {
      ...state.userBankAccountList,
      status: 'LOADING',
    },
  }))
  .handleAction(getUserBankAccountListSuccess, (state, { payload }) => ({
    ...state,
    userBankAccountList: {
      ...state.userBankAccountList,
      status: 'SUCCESS',
      data: payload,
    },
  }))
  .handleAction(getUserBankAccountListFailure, (state, { payload }) => ({
    ...state,
    userBankAccountList: {
      ...state.userBankAccountList,
      status: 'ERROR',
      error: payload,
    },
  }))
  .handleAction(getClientTypesForAssignToUserRequest, (state) => ({
    ...state,
    clientTypesForAssignToUser: {
      ...state.clientTypesForAssignToUser,
      status: 'LOADING',
    },
  }))
  .handleAction(getClientTypesForAssignToUserSuccess, (state, { payload }) => ({
    ...state,
    clientTypesForAssignToUser: {
      ...state.clientTypesForAssignToUser,
      status: 'SUCCESS',
      data: payload,
    },
  }))
  .handleAction(getClientTypesForAssignToUserFailure, (state, { payload }) => ({
    ...state,
    clientTypesForAssignToUser: {
      ...state.clientTypesForAssignToUser,
      status: 'ERROR',
      error: payload,
    },
  }))
